import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import GreenPremiumCalculator from "@/components/GreenPremiumCalculator";
import { Link } from "react-router-dom";
import { applySeo } from "@/lib/seo";

export default function CalculatorGreenPremium() {
  useEffect(() => {
    applySeo({
      title: "Green Premium Rechner – Grüne Geldanlage",
      description: "Wie viel Rendite kostet nachhaltiges Investieren wirklich? Der Green Premium Rechner vergleicht Endwert und Opportunitätskosten klassischer und nachhaltiger Portfolios.",
      canonicalPath: "/rechner/green-premium",
    });
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-green-premium-page">
      <Header />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="overline mb-4">Rechner</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          Green Premium Rechner
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl mb-10">
          Wie viel Rendite „kostet“ ein gutes Gewissen wirklich? Der Green Premium Rechner
          vergleicht ein klassisches Welt-ETF-Portfolio mit einem nachhaltigen Pendant – auf
          Basis Ihrer eigenen Annahmen für Rendite und Gesamtkostenquote (TER).
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="calc-green-premium-tool">
        <GreenPremiumCalculator />
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="prose-editorial max-w-none">
          <h2>Das „Green Premium“: Was steckt hinter dem Begriff?</h2>
          <p>
            Der Begriff „Green Premium“ stammt ursprünglich aus der Klimaökonomie und beschreibt
            die Mehrkosten, die für eine nachhaltige Alternative gegenüber einer konventionellen
            Variante anfallen. Auf die Geldanlage übertragen bedeutet er: Wie viel Rendite
            verzichte ich – theoretisch – wenn ich von einem klassischen MSCI World ETF auf ein
            nachhaltiges Pendant wie den MSCI World SRI oder ein Paris-Aligned-Produkt wechsle?
            Der Rechner beantwortet diese Frage konkret in Euro über einen frei wählbaren
            Anlagehorizont.
          </p>

          <h2>Warum diese Rechnung überhaupt sinnvoll ist</h2>
          <p>
            Viele Anleger zögern beim Wechsel zu nachhaltigen ETFs, weil sie befürchten, damit
            langfristig Rendite zu verlieren. Diese Sorge ist verständlich, aber häufig
            überzeichnet: Historische Renditeunterschiede zwischen nachhaltigen und klassischen
            Weltmarkt-ETFs bewegen sich meist im Bereich von wenigen Zehntelprozentpunkten pro
            Jahr – manchmal zugunsten der nachhaltigen Variante, manchmal zugunsten der
            klassischen. Auch die TER-Differenzen sind in den letzten Jahren stark gesunken:
            Nachhaltige Welt-ETFs sind heute für 0,20 bis 0,30 Prozent verfügbar, gegenüber
            0,10 bis 0,20 Prozent bei klassischen Pendants. Der Rechner macht diese
            Differenzen greifbar, statt sie in gefühlten Prozenten zu diskutieren.
          </p>

          <h2>Wie funktioniert die Berechnung?</h2>
          <p>
            Zwei parallele Sparpläne werden simuliert: einer mit Ihren Annahmen für das
            klassische Portfolio (Nominalrendite und TER), einer für das nachhaltige. Aus der
            Nominalrendite abzüglich der TER ergibt sich die Nettorendite; diese wird monatlich
            auf den bereits vorhandenen Kapitalstock und die neue Sparrate angewendet. So
            entstehen zwei Vermögenskurven, deren Differenz am Ende der Laufzeit die
            „Kosten des guten Gewissens“ – oder gegebenenfalls den Vorsprung der nachhaltigen
            Variante – exakt beziffert.
          </p>

          <h2>Realistische Eingabewerte</h2>
          <p>
            Für einen breiten Weltmarkt-ETF (klassisch) lassen sich langfristige Nettorenditen
            von etwa 6 bis 8 Prozent pro Jahr diskutieren, bei einer TER von 0,10 bis 0,20 Prozent.
            Für nachhaltige Weltmarkt-ETFs (SRI, ESG-Leaders, Paris-Aligned) sind Renditen von
            5,5 bis 7,5 Prozent bei einer TER von 0,20 bis 0,35 Prozent realistisch. Diese
            Werte variieren jedoch je nach Indexmethodik, Region und Marktphase. Experimentieren
            Sie ruhig mit Extremannahmen: Was passiert, wenn die nachhaltige Variante
            0,5 Prozentpunkte Rendite hinterherhinkt? Wie stark verschiebt sich das Bild bei
            gleicher TER? Die Ergebnisse überraschen viele Anleger.
          </p>

          <h2>Die Nettorendite ist entscheidend</h2>
          <p>
            Die wichtigste Erkenntnis aus dem Green Premium Rechner ist meist nicht die Höhe der
            Differenz, sondern ihre Relation zum Gesamtvermögen. Bei einem monatlichen Sparplan
            über 200 Euro und einer Laufzeit von 20 Jahren bewegt sich die Differenz zwischen
            einer nachhaltigen und einer klassischen Variante mit vergleichbaren Rendite- und
            Kostenannahmen typischerweise im niedrigen einstelligen Prozentbereich des
            Endkapitals. In vielen Fällen ist die Differenz kleiner als der Effekt einer
            einzigen Steueränderung oder eines schlecht getimten Ein- oder Ausstiegs. Wer
            Nachhaltigkeit als Wertentscheidung ernst nimmt, kann diese Zahl bewusst
            akzeptieren – oder feststellen, dass sie kleiner ist als vermutet.
          </p>

          <h2>Was der Rechner bewusst nicht abbildet</h2>
          <p>
            Der Green Premium Rechner arbeitet mit einer konstanten Nettorendite und einer
            konstanten TER. In der Realität schwanken beide: TERs können sich durch Fondswechsel
            ändern, Renditen sind naturgemäß volatil. Auch Steuern und Vorabpauschale sind
            ausgeklammert; Aktien-ETFs profitieren in Deutschland von der Teilfreistellung von
            30 Prozent, was den Nachsteuerabstand tendenziell verringert. Die Rechnung
            berücksichtigt außerdem keine qualitativen Faktoren wie Impact-Wirkung oder
            regulatorische Risiken emissionsstarker Sektoren, die klassische Indizes stärker
            belasten könnten. Vertiefende Einordnungen dazu finden Sie in unserem Artikel
            {" "}<Link to="/artikel/nachhaltige-etfs-vs-gruene-fonds">Nachhaltige ETFs vs. aktiv gemanagte grüne Fonds</Link>.
          </p>

          <h2>Praxis-Tipp: Kombinieren Sie die drei Rechner</h2>
          <p>
            Der Green Premium Rechner liefert die finanzielle Perspektive; der
            {" "}<Link to="/rechner/nachhaltigkeits-rendite">Nachhaltigkeits-Rendite-Rechner</Link>{" "}
            zeigt Ihnen die absolute Vermögensentwicklung; und der
            {" "}<Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck-Portfolio-Rechner</Link>{" "}
            fügt die ökologische Wirkung hinzu. Erst zusammen entsteht ein vollständiges Bild –
            und die Entscheidung, welche Balance zwischen Rendite, Kosten und Wirkung für Sie
            persönlich stimmig ist.
          </p>
          <p>
            Noch unsicher, welcher nachhaltige ETF überhaupt zu Ihnen passt? Unser{" "}
            <Link to="/rechner/nachhaltiger-etf-finder">ESG-Fit-Quiz</Link> filtert aus
            25 kuratierten ETFs die passende Empfehlung für Ihre Kriterien.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
